import React, { JSX, useCallback, useRef, useState } from 'react';
import SingleSelection from '../../Components/SingleSelection';
import { PackageDropdownFilter, PackageSearchParams, useConfig } from '@ibe/components';
import { PackageCode, ProductIBEProps } from './PackagesList';
import { ApiBestPricePackageModel, ApiGeoUnit, ApiPostalCode, ApiRentalCarOffice } from '@ibe/api';
import { PromotionCodeData } from '../../Components/PromotionCode';
import {
  MINI_GROUP,
  MINI_GROUP_STR_DE,
  SMALL_GROUP,
  SMALL_GROUP_STR_DE
} from '../../Util/constant';
import { packageFilterSessionStorage } from '../../Pages/Util/utils';
import { GlobalFilterParams } from './PBusFilters';
import { getEarliestPossibleDate } from './usePackagesListFunctions';
import CanonicalHead from '../Util/CanonicalHead';

export type FilterParams = {
  numberOfTravelers: number;
  origins: string[];
  busStations: string[];
  startDate: string;
  endDate: string;
  groupSize?: string;
  selectedPostalData?: ApiPostalCode;
};

export type InitialStorageFilters = {
  initialFilters?: GlobalFilterParams;
  initialFiltersChecked: boolean;
};

const filterPropsMapper = (
  groupSize?: string,
  searchParams?: Partial<PackageSearchParams>
): FilterParams => {
  return {
    numberOfTravelers: searchParams?.occupancy ? searchParams.occupancy[0].adults : 0,
    origins: searchParams?.origins?.map((origin: ApiGeoUnit) => origin.id) || [],
    busStations:
      (searchParams?.busStations
        ?.map((office: ApiRentalCarOffice) => office.code)
        .filter((code): code is string => code !== undefined) as string[]) || [],
    startDate: searchParams?.startDate || '',
    endDate: searchParams?.endDate || '',
    groupSize
  } as FilterParams;
};

const Filters = ({
  earliestPossibleDate,
  latestPossibleDate,
  onChange,
  origins,
  packageCodes,
  maintenanceMode,
  numberOfTravelers,
  packages = [],
  promotionCode,
  initialPackageSearchParams
}: {
  earliestPossibleDate: string;
  latestPossibleDate: string;
  onChange: (filters: FilterParams, ignoreNumberOfTravelers?: boolean) => void;
  origins: string[];
  packageCodes: ProductIBEProps['packageCodes'];
  maintenanceMode?: boolean;
  numberOfTravelers: number;
  packages: ApiBestPricePackageModel[];
  promotionCode: PromotionCodeData;
  initialPackageSearchParams?: PackageSearchParams;
}): JSX.Element => {
  type Mode = 'selectsStart' | 'selectsEnd' | 'none';
  const dateRangeRef = useRef<{
    focus?: (a: Mode) => void;
  }>({
    focus: () => {}
  });
  const config = useConfig();

  const [cachedFilter] = useState<GlobalFilterParams>(
    hasCachedValue(packageCodes, earliestPossibleDate, latestPossibleDate)
  );

  const getInitialGroupSizes = (): string[] => {
    const searchParams = new URLSearchParams(window.location.search);
    const initialSizes: string[] = [];

    const hasKleingruppe =
      searchParams.has(SMALL_GROUP_STR_DE) && searchParams.get(SMALL_GROUP_STR_DE) !== 'false';
    const hasMinigruppe =
      searchParams.has(MINI_GROUP_STR_DE) && searchParams.get(MINI_GROUP_STR_DE) !== 'false';

    if (hasKleingruppe) initialSizes.push(SMALL_GROUP);
    if (hasMinigruppe) initialSizes.push(MINI_GROUP);

    if (initialSizes.length === 0 && cachedFilter?.groupSize) {
      initialSizes.push(...cachedFilter.groupSize.split(','));
    }

    return initialSizes;
  };

  const [selectedGroupSizes, setSelectedGroupSizes] = useState<string[]>(getInitialGroupSizes);

  const [packageSearchParams, setPackageSearchParams] = useState<PackageSearchParams | undefined>(
    initialPackageSearchParams
  );

  const handleFilterChange = useCallback(
    (searchParams: Partial<PackageSearchParams>): void => {
      setPackageSearchParams(searchParams);
      onChange(filterPropsMapper(selectedGroupSizes.join(','), searchParams));
    },
    [onChange, promotionCode, selectedGroupSizes]
  );

  const handleGroupSizeChecked = useCallback(
    (grpSize: string): void => {
      let updatedSizes: string[];
      if (selectedGroupSizes.includes(grpSize)) {
        updatedSizes = selectedGroupSizes.filter(size => size !== grpSize);
      } else {
        updatedSizes = [...selectedGroupSizes, grpSize];
      }

      setSelectedGroupSizes(updatedSizes);
      const combinedGroupSize = updatedSizes.length > 0 ? updatedSizes.join(',') : undefined;

      const currentSearchParams = packageSearchParams ??
        initialPackageSearchParams ?? {
          startDate: getEarliestPossibleDate(
            cachedFilter.startDate || earliestPossibleDate,
            config
          ),
          endDate: cachedFilter.endDate || latestPossibleDate,
          occupancy: [
            {
              adults: numberOfTravelers,
              children: 0,
              infants: 0,
              childrenAges: [],
              infantsAges: []
            }
          ],
          origins: []
        };

      const finalFilters = filterPropsMapper(combinedGroupSize, currentSearchParams);
      onChange(finalFilters, true);
    },
    [
      onChange,
      promotionCode,
      origins,
      packageSearchParams,
      initialPackageSearchParams,
      selectedGroupSizes,
      cachedFilter,
      numberOfTravelers,
      earliestPossibleDate,
      latestPossibleDate,
      config
    ]
  );

  const onToggle = useCallback(
    ({ isOpen, endDate, startDate }: { isOpen?: boolean; endDate?: Date; startDate?: Date }) => {
      if (isOpen && !endDate && startDate && dateRangeRef && dateRangeRef.current?.focus) {
        dateRangeRef.current?.focus('selectsEnd');
      }
    },
    [dateRangeRef]
  );

  const distinctGroupSizes = Array.from(new Set(packageCodes.map(c => c.groupSize)));

  return (
    <>
      <CanonicalHead />
      {distinctGroupSizes.includes(SMALL_GROUP) &&
        distinctGroupSizes.length > 1 &&
        !maintenanceMode && (
          <SingleSelection
            selectionId="small-group-selection"
            groupSizeChecked={selectedGroupSizes.includes(SMALL_GROUP)}
            setIsChecked={() => handleGroupSizeChecked(SMALL_GROUP)}
            groupSize={SMALL_GROUP}
            promotionCode={promotionCode}
            showInfoTextInFront
          />
        )}

      {distinctGroupSizes.includes(MINI_GROUP) &&
        distinctGroupSizes.length > 1 &&
        !maintenanceMode && (
          <SingleSelection
            selectionId="mini-group-selection"
            groupSizeChecked={selectedGroupSizes.includes(MINI_GROUP)}
            setIsChecked={() => handleGroupSizeChecked(MINI_GROUP)}
            groupSize={MINI_GROUP}
            promotionCode={promotionCode}
            showInfoTextInFront
          />
        )}
      <div className="all-filter-container mb-3">
        <PackageDropdownFilter
          onChange={handleFilterChange}
          datepickerMaxDate={latestPossibleDate}
          datepickerMinDate={earliestPossibleDate}
          origins={cachedFilter.possibleOrigins || origins}
          disabled={maintenanceMode}
          defaultDate={{
            startDate: getEarliestPossibleDate(
              cachedFilter.startDate || earliestPossibleDate,
              config
            ),
            endDate: cachedFilter.endDate || latestPossibleDate
          }}
          onToggle={{
            calendar: onToggle
          }}
          dateRangeRef={dateRangeRef}
          numberOfTravelers={numberOfTravelers}
          memoDeps={[promotionCode, origins]}
          preSelectedOrigins={cachedFilter.origins}
          selectAllOriginsOnInitialLoad
        />
      </div>
    </>
  );
};

export default Filters;

export const hasCachedValue = (
  packageCodes: PackageCode[],
  earliestPossibleDate: string,
  latestPossibleDate: string
) => {
  const previouslyUsedFilters = packageFilterSessionStorage.get();
  if (
    previouslyUsedFilters?.packageCodes === JSON.stringify(packageCodes) &&
    earliestPossibleDate === previouslyUsedFilters.earliestPossibleDate &&
    latestPossibleDate === previouslyUsedFilters.latestPossibleDate
  ) {
    return previouslyUsedFilters;
  } else {
    packageFilterSessionStorage.clear();
  }
  return {} as GlobalFilterParams;
};
